import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileSearch, House, LifeBuoy, SearchX } from "lucide-react";
import "@/styles/landing.css";

const RECOVERY_LINKS = [
  { to: "/", label: "Home", description: "Return to the starting point", icon: House },
  { to: "/intake", label: "Resume analysis", description: "Get your free career score", icon: FileSearch },
  { to: "/contact", label: "Help center", description: "Tell us what you were looking for", icon: LifeBuoy },
];

export default function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const headingRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Page not found | PlacementOS";
    headingRef.current?.focus({ preventScroll: true });
    return () => { document.title = previousTitle; };
  }, []);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <main className="ap-root" data-testid="not-found-page">
      <section className="ap-mesh ap-grid-overlay relative min-h-[calc(100vh-4rem)] overflow-hidden pt-28 pb-20 flex items-center">
        <div className="ap-section w-full">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div className="max-w-[660px]">
              <span
                className="ap-chip ap-rise ap-rise-1"
                style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}
              >
                <SearchX className="w-4 h-4" /> Error 404
              </span>

              <h1
                ref={headingRef}
                tabIndex="-1"
                className="ap-display font-extrabold text-[var(--ap-ink)] mt-6 ap-rise ap-rise-2 focus:outline-none"
                style={{ fontSize: "clamp(42px,7vw,78px)", lineHeight: 0.98, letterSpacing: "-0.045em" }}
              >
                This page missed<br />
                <span className="ap-grad-text">the shortlist.</span>
              </h1>

              <p className="text-[16px] md:text-[18px] text-[var(--ap-muted)] leading-relaxed mt-6 max-w-[580px] ap-rise ap-rise-3">
                The link may be outdated, or the page may have moved. PlacementOS is still here to help you find the right next step.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-8 ap-rise ap-rise-4">
                <Link to="/" className="ap-btn ap-btn-pri ap-btn-lg">
                  Back to home <ArrowRight className="w-[18px] h-[18px]" />
                </Link>
                <button type="button" onClick={goBack} className="ap-btn ap-btn-ghost ap-btn-lg">
                  <ArrowLeft className="w-[18px] h-[18px]" /> Go back
                </button>
              </div>
            </div>

            <div className="relative w-full max-w-[480px] mx-auto lg:mr-0 ap-rise ap-rise-3">
              <div className="absolute -inset-8 rounded-full bg-[#ECE8FE]/80 blur-3xl" aria-hidden="true" />
              <div className="ap-card ap-glass relative overflow-hidden p-5 sm:p-7">
                <div className="flex items-center justify-between pb-5 border-b border-[var(--ap-line)]">
                  <div className="flex gap-2" aria-hidden="true">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#D9D5F7]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#BEB7F2]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[var(--ap-violet)]" />
                  </div>
                  <span className="font-mono text-[11px] font-semibold tracking-[0.16em] text-[var(--ap-muted)] uppercase">Route check</span>
                </div>

                <div className="py-7 text-center">
                  <div className="w-16 h-16 rounded-2xl mx-auto grid place-items-center bg-[var(--ap-violet-soft)] text-[var(--ap-violet)]">
                    <SearchX className="w-8 h-8" />
                  </div>
                  <p className="ap-display text-[56px] sm:text-[72px] leading-none font-extrabold text-[var(--ap-ink)] mt-5">404</p>
                  <p className="text-sm text-[var(--ap-muted)] mt-3">No page exists at</p>
                  <p className="font-mono text-xs sm:text-sm text-[var(--ap-ink-2)] bg-[var(--ap-canvas)] border border-[var(--ap-line)] rounded-xl px-4 py-3 mt-3 truncate">
                    {location.pathname}
                  </p>
                </div>

                <div className="grid gap-2.5" aria-label="Useful links">
                  {RECOVERY_LINKS.map(({ to, label, description, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      className="group flex items-center gap-3 rounded-2xl border border-[var(--ap-line)] bg-white px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:border-[#BEB7F2] hover:shadow-[var(--ap-shadow-sm)]"
                    >
                      <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-[var(--ap-violet-soft)] text-[var(--ap-violet)]">
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block ap-display text-sm font-bold text-[var(--ap-ink)]">{label}</span>
                        <span className="block text-xs text-[var(--ap-muted)] mt-0.5 truncate">{description}</span>
                      </span>
                      <ArrowRight className="w-4 h-4 ml-auto shrink-0 text-[var(--ap-muted)] group-hover:text-[var(--ap-violet)] transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
